/**
 * Project format migrations.
 *
 * Each step upgrades one version to the next and is pure (object in, object
 * out), so loading an old file is a fold over the step list and adding a
 * format change later means appending one function rather than touching the
 * loader.
 */

export const CURRENT_VERSION = 1;

export type RawProject = Record<string, unknown>;

export interface MigrationStep {
  from: number;
  to: number;
  describe: string;
  migrate(raw: RawProject): RawProject;
}

export const MIGRATIONS: readonly MigrationStep[] = [
  // Version 1 is the initial format. Steps are appended here as the format
  // evolves, e.g.:
  // { from: 1, to: 2, describe: 'layer opacity', migrate: (r) => ({ ...r, ... }) },
];

export function migrateProject(raw: RawProject, warnings: string[]): RawProject {
  let version = Math.trunc(Number(raw.version));
  if (!Number.isFinite(version) || version < 1) {
    warnings.push('Project had no usable version number; assuming version 1.');
    version = 1;
  }
  if (version > CURRENT_VERSION) {
    warnings.push(
      `Project was saved by a newer version (format ${version}, this build understands ${CURRENT_VERSION}). Unknown fields are ignored.`,
    );
    return { ...raw, version: CURRENT_VERSION };
  }

  let current = raw;
  while (version < CURRENT_VERSION) {
    const step = MIGRATIONS.find((s) => s.from === version);
    if (!step) {
      warnings.push(`No migration path from format ${version}; loading as-is.`);
      break;
    }
    current = step.migrate(current);
    version = step.to;
    warnings.push(`Migrated project to format ${version} (${step.describe}).`);
  }
  return { ...current, version: CURRENT_VERSION };
}
